import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import ReactMarkdown from "react-markdown";
import { Mic, Send, House, Grid3x3, Phone, X, Calendar, Download, Users, ArrowRight, Check, Volume2, VolumeX, Plus, Keyboard, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/* ============================================================
   Waveform (bars) — for both the animated orb and idle state
   ============================================================ */
function Waveform({ active }) {
  return (
    <div className="flex items-center justify-center gap-1.5 h-14">
      {[0.4, 0.7, 1, 0.7, 0.4].map((s, i) => (
        <div
          key={i}
          className={`w-1.5 rounded-full bg-white ${active ? "wave-bar" : ""}`}
          style={{
            height: `${s * 60}%`,
            animationDelay: `${i * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ============================================================
   Voice recorder using MediaRecorder — sends webm to backend Whisper
   ============================================================ */
function useRecorder(onResult) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/public/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) onResult(data.text);
          else toast.error("Sorry, I didn't catch that. Try again.");
        } catch (err) {
          toast.error("Voice transcription failed. Please type your question.");
        } finally {
          setProcessing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied. Please type your question.");
    }
  }, [onResult]);

  const stop = useCallback(() => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  return { recording, processing, start, stop };
}

/* ============================================================
   Main visitor experience — matches attached mockup
   ============================================================ */
export function VisitorEvent() {
  const { orgSlug, eventSlug } = useParams();
  return <VisitorExperience orgSlug={orgSlug} eventSlug={eventSlug} />;
}

export function VisitorOrgLanding() {
  const { orgSlug } = useParams();
  return <VisitorExperience orgSlug={orgSlug} eventSlug={null} />;
}

const ACTION_META = {
  book_demo:         { icon: <Calendar size={14} />, label: "Book a Demo" },
  download_brochure: { icon: <Download size={14} />, label: "Download Brochure" },
  get_more_info:     { icon: <Users size={14} />,    label: "Get more info" },
  talk_to_sales:     { icon: <Users size={14} />,    label: "Get more info" },
  request_pricing:   { icon: <Calendar size={14} />, label: "Book a Demo" },
};

function VisitorExperience({ orgSlug, eventSlug }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screen, setScreen] = useState("welcome"); // welcome | listening | chat | lead | thanks
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [idleCountdown, setIdleCountdown] = useState(null); // number 5..0 or null
  const sessionIdRef = useRef(`s-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const url = eventSlug
          ? `/public/event/${orgSlug}/${eventSlug}`
          : `/public/org/${orgSlug}`;
        const { data } = await api.get(url);
        setCtx(data);
        if (data.organization?.voice_enabled === false) setVoiceOn(false);
      } catch (e) {
        setError(e?.response?.data?.detail || "Not available");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgSlug, eventSlug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, screen]);

  /* ============ Auto-reset after 30s idle → 5s countdown ============ */
  const resetConversation = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
    setMessages([]);
    setInput("");
    setPendingAction(null);
    setIdleCountdown(null);
    setScreen("welcome");
    sessionIdRef.current = `s-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }, []);

  const clearIdleTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    setIdleCountdown(null);
  }, []);

  const scheduleIdle = useCallback(() => {
    clearIdleTimers();
    // Only auto-reset when the visitor is actually in a conversation
    if (screen === "welcome" || messages.length === 0) return;
    idleTimerRef.current = setTimeout(() => {
      // start 5s countdown
      let n = 5;
      setIdleCountdown(n);
      countdownTimerRef.current = setInterval(() => {
        n -= 1;
        if (n <= 0) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          resetConversation();
        } else {
          setIdleCountdown(n);
        }
      }, 1000);
    }, 30000);
  }, [screen, messages.length, clearIdleTimers, resetConversation]);

  const bumpActivity = useCallback(() => {
    scheduleIdle();
  }, [scheduleIdle]);

  // Re-arm timer whenever conversation state changes
  useEffect(() => {
    scheduleIdle();
    return () => clearIdleTimers();
  }, [screen, messages, scheduleIdle, clearIdleTimers]);

  const rec = useRecorder((text) => {
    setScreen("chat");
    sendMessage(text);
  });

  async function sendMessage(text) {
    if (!text.trim() || !ctx) return;
    bumpActivity();
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    try {
      const { data } = await api.post("/public/chat", {
        org_slug: orgSlug,
        event_slug: eventSlug || null,
        session_id: sessionIdRef.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.answer, actions: data.suggested_actions || [] }]);
      // Auto-play TTS if voice is enabled
      if (voiceOn && ctx.organization?.voice_enabled !== false) {
        try {
          const audioRes = await api.post(
            "/public/tts",
            { org_slug: orgSlug, text: data.answer, voice: "nova" },
            { responseType: "blob" }
          );
          const url = URL.createObjectURL(audioRes.data);
          if (audioRef.current) {
            audioRef.current.pause();
            URL.revokeObjectURL(audioRef.current.src);
          }
          audioRef.current = new Audio(url);
          audioRef.current.play().catch(() => {});
        } catch {
          /* TTS best-effort; do not block */
        }
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I ran into a problem. Please try again." }]);
    } finally {
      setSending(false);
    }
  }

  function toggleVoice() {
    if (voiceOn && audioRef.current) {
      audioRef.current.pause();
    }
    setVoiceOn((v) => !v);
  }

  function askSuggested(q) {
    setScreen("chat");
    sendMessage(q);
  }

  function openLeadFlow(action) {
    setPendingAction(action);
    setScreen("lead");
  }

  if (loading) {
    return (
      <div className="visitor-bg min-h-screen flex items-center justify-center text-white/80">
        Loading…
      </div>
    );
  }
  if (error || !ctx) {
    return (
      <div className="visitor-bg min-h-screen flex flex-col items-center justify-center text-white p-8 text-center">
        <div className="text-2xl font-display font-semibold mb-3">Not available</div>
        <div className="text-white/70 text-sm">This experience is not currently active.</div>
      </div>
    );
  }

  const org = ctx.organization;
  const event = ctx.event || null;
  const bannerBg = org.banner_path ? `url(${fileUrl(org.banner_path)})` : null;

  return (
    <div className="visitor-bg min-h-screen sm:flex sm:items-center sm:justify-center">
      <div
        className="w-full min-h-screen sm:min-h-0 sm:h-[840px] sm:max-w-[420px] sm:rounded-[2.5rem] sm:border-[10px] sm:border-black sm:overflow-hidden sm:shadow-2xl relative flex flex-col text-white"
        data-testid="visitor-shell"
        style={bannerBg ? { backgroundImage: bannerBg, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        onPointerDownCapture={bumpActivity}
        onKeyDownCapture={bumpActivity}
      >
        {bannerBg && <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-purple-900/85 to-purple-950/95 pointer-events-none" />}

        <div className="relative flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            {org.logo_path ? (
              <img src={fileUrl(org.logo_path)} alt={org.name} className="h-8 max-w-[140px] object-contain" />
            ) : (
              <Logo className="h-7" variant="light" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleVoice}
              className="text-white/70 hover:text-white bg-white/5 rounded-full p-2 border border-white/10"
              data-testid="visitor-voice-toggle"
              title={voiceOn ? "Mute voice replies" : "Enable voice replies"}
            >
              {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
              onClick={() => { bumpActivity(); resetConversation(); }}
              className="text-white/70 hover:text-white bg-white/5 rounded-full p-2 border border-white/10"
              data-testid="new-conversation-btn"
              title="Start a new conversation"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Organization strip */}
        <div className="px-5 mb-3 shrink-0">
          <div className="text-xs uppercase tracking-[0.2em] text-white/50">Powered by ExpoFlo AI</div>
          <h1 className="font-display text-2xl font-bold mt-1" data-testid="visitor-org-name">{org.name}</h1>
          {org.description && <p className="text-sm text-white/70 mt-1 leading-relaxed">{org.description}</p>}
          {event && <div className="text-xs text-white/50 mt-2">📍 {event.venue || event.name}</div>}
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 pb-24 hide-scrollbar">

          {screen === "welcome" && (
            <div className="fade-up bg-black/30 border border-white/10 rounded-2xl p-6 mt-2">
              <div className="text-lg font-display font-semibold flex items-center gap-2">
                Hi there! <span>👋</span>
              </div>
              <p className="text-sm text-white/80 mt-2 leading-relaxed">
                {org.welcome_message || `I'm ${org.assistant_name || "your assistant"}. Ask me anything about ${org.name}.`}
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  onClick={() => { bumpActivity(); setScreen("chat"); }}
                  className="brand-gradient text-white rounded-2xl py-5 flex flex-col items-center justify-center gap-2 hover:brightness-110 transition-all border border-white/10"
                  data-testid="ask-by-text-btn"
                >
                  <Keyboard size={22} />
                  <span className="text-sm font-medium">Ask by Text</span>
                </button>
                <button
                  onClick={() => { bumpActivity(); setScreen("listening"); }}
                  className="brand-gradient text-white rounded-2xl py-5 flex flex-col items-center justify-center gap-2 hover:brightness-110 transition-all border border-white/10"
                  data-testid="ask-by-voice-btn"
                >
                  <Mic size={22} />
                  <span className="text-sm font-medium">Ask by Voice</span>
                </button>
              </div>

              {(org.suggested_questions || []).length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Try asking</div>
                  <div className="flex flex-wrap gap-2">
                    {org.suggested_questions.map((q) => (
                      <button
                        key={q}
                        onClick={() => askSuggested(q)}
                        className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition-colors"
                        data-testid={`suggested-${q.slice(0, 20)}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {screen === "listening" && (
            <div className="fade-up flex flex-col items-center justify-center min-h-[500px]">
              <div className="text-sm text-white/70 uppercase tracking-[0.2em] mb-8">
                {rec.processing ? "Processing…" : rec.recording ? "Listening…" : "Ready"}
              </div>
              <div className="relative w-52 h-52 flex items-center justify-center">
                {rec.recording && (
                  <>
                    <div className="absolute inset-0 rounded-full brand-gradient orb-ring opacity-40" />
                    <div className="absolute inset-4 rounded-full brand-gradient orb-ring opacity-30" style={{ animationDelay: "0.4s" }} />
                  </>
                )}
                <div className={`relative w-40 h-40 rounded-full brand-gradient flex items-center justify-center ${rec.recording ? "orb-pulse" : ""}`}>
                  <Waveform active={rec.recording} />
                </div>
              </div>
              <button
                onClick={rec.recording ? rec.stop : rec.start}
                disabled={rec.processing}
                className="mt-10 bg-white text-purple-900 rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="tap-to-record"
              >
                <Mic size={16} />
                {rec.processing ? "Transcribing…" : rec.recording ? "Tap to stop" : "Tap to speak"}
              </button>
              <button onClick={() => setScreen("welcome")} className="mt-4 text-xs text-white/50 underline underline-offset-4">
                Cancel
              </button>
            </div>
          )}

          {screen === "chat" && (
            <div className="fade-up space-y-3 pt-2">
              {messages.length === 0 && (
                <div className="text-white/60 text-sm text-center py-8">
                  Ask anything about {org.name}.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex flex-col items-start"}>
                  <div
                    className={
                      m.role === "user"
                        ? "brand-gradient rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%] text-sm"
                        : "bg-black/40 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] text-sm leading-relaxed"
                    }
                  >
                    {m.role === "assistant" ? (
                      <div className="markdown-body">
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
                            ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1" {...p} />,
                            ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...p} />,
                            li: ({ node, ...p }) => <li className="text-sm" {...p} />,
                            strong: ({ node, ...p }) => <strong className="font-semibold text-pink-300" {...p} />,
                            em: ({ node, ...p }) => <em className="italic text-white/90" {...p} />,
                            h1: ({ node, ...p }) => <h3 className="font-display text-base font-semibold mt-1 mb-1.5" {...p} />,
                            h2: ({ node, ...p }) => <h3 className="font-display text-base font-semibold mt-1 mb-1.5" {...p} />,
                            h3: ({ node, ...p }) => <h3 className="font-display text-sm font-semibold mt-1 mb-1" {...p} />,
                            a: ({ node, ...p }) => <a className="text-pink-300 underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
                            code: ({ node, ...p }) => <code className="bg-black/40 rounded px-1 py-0.5 text-[0.8em]" {...p} />,
                          }}
                        >
                          {m.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      m.text
                    )}
                  </div>
                  {m.role === "assistant" && (m.actions || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 max-w-[85%]" data-testid={`msg-actions-${i}`}>
                      {m.actions.map((a) => {
                        const meta = ACTION_META[a] || { icon: <ArrowRight size={14} />, label: a };
                        return (
                          <button
                            key={a}
                            onClick={() => openLeadFlow(a)}
                            className="text-xs bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                            data-testid={`inline-action-${a}`}
                          >
                            {meta.icon} {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-black/30 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white/60">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {screen === "lead" && (
            <LeadForm
              ctx={ctx}
              orgSlug={orgSlug}
              eventSlug={eventSlug}
              sessionId={sessionIdRef.current}
              action={pendingAction}
              onCancel={() => setScreen("chat")}
              onSuccess={() => setScreen("thanks")}
            />
          )}

          {screen === "thanks" && (
            <div className="fade-up bg-black/30 border border-white/10 rounded-2xl p-8 mt-6 text-center">
              <div className="w-14 h-14 rounded-full brand-gradient mx-auto flex items-center justify-center mb-4">
                <Check size={26} />
              </div>
              <div className="font-display text-xl font-semibold">Thank you!</div>
              <p className="text-sm text-white/70 mt-2">Your request has been received. The {org.name} team will follow up shortly.</p>
              <button
                onClick={() => { setScreen("chat"); setPendingAction(null); }}
                className="mt-6 text-sm text-white/70 underline underline-offset-4"
                data-testid="ask-another-btn"
              >
                Ask another question
              </button>
            </div>
          )}
        </div>

        {/* Chat input (bottom, only in chat screen) */}
        {screen === "chat" && (
          <div className="absolute bottom-16 left-0 right-0 px-4">
            <form
              onSubmit={(e) => { e.preventDefault(); if (input.trim()) sendMessage(input.trim()); }}
              className="flex items-center gap-2 bg-black/50 border border-white/15 rounded-full px-4 py-2 backdrop-blur-sm"
            >
              <input
                value={input}
                onChange={(e) => { setInput(e.target.value); bumpActivity(); }}
                onFocus={bumpActivity}
                placeholder="Ask another question…"
                className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 outline-none"
                data-testid="chat-text-input"
              />
              <button type="button" onClick={rec.recording ? rec.stop : rec.start} className="text-white/70 hover:text-white" data-testid="chat-mic-btn">
                <Mic size={18} className={rec.recording ? "text-pink-400" : ""} />
              </button>
              <button type="submit" className="brand-gradient rounded-full p-1.5" data-testid="chat-send-btn">
                <Send size={14} />
              </button>
            </form>
          </div>
        )}

        {/* Bottom nav */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 border-t border-white/10 backdrop-blur-md">
          <div className="grid grid-cols-3 py-3">
            <NavBtn active={screen === "welcome" || screen === "listening"} icon={<House size={18} />} label="Home" onClick={() => { bumpActivity(); setScreen("welcome"); }} testId="nav-home" />
            <NavBtn active={screen === "chat" || screen === "lead" || screen === "thanks"} icon={<Grid3x3 size={18} />} label="Chat" onClick={() => { bumpActivity(); setScreen("chat"); }} testId="nav-solutions" />
            <NavBtn icon={<Phone size={18} />} label="Contact" onClick={() => { bumpActivity(); openLeadFlow("get_more_info"); }} testId="nav-contact" />
          </div>
        </div>

        {/* Idle countdown overlay */}
        {idleCountdown !== null && (
          <div
            className="absolute inset-0 z-30 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center px-8 text-center"
            data-testid="idle-countdown-overlay"
            onClick={() => { clearIdleTimers(); bumpActivity(); }}
          >
            <div className="text-xs uppercase tracking-[0.24em] text-white/60 mb-3">Are you still there?</div>
            <div className="font-display text-3xl font-semibold" data-testid="idle-countdown-text">
              Starting a fresh conversation in {idleCountdown}...
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); clearIdleTimers(); bumpActivity(); }}
              className="mt-6 bg-white text-purple-900 rounded-full px-5 py-2 text-sm font-medium"
              data-testid="idle-stay-btn"
            >
              I'm still here
            </button>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">Tap anywhere to cancel</div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 text-sm transition-colors"
    >
      <span className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      <ArrowRight size={14} className="opacity-60" />
    </button>
  );
}

function NavBtn({ active, icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-col items-center gap-0.5 text-[10px] uppercase tracking-wider ${active ? "text-white" : "text-white/50"}`}
    >
      {icon}
      {label}
    </button>
  );
}

function LeadForm({ ctx, orgSlug, eventSlug, sessionId, action, onCancel, onSuccess }) {
  const [form, setForm] = useState({ full_name: "", company: "", email: "", phone: "", area_of_interest: "" });
  const [submitting, setSubmitting] = useState(false);

  const labels = {
    book_demo: "Book a demo",
    download_brochure: "Get the brochure",
    get_more_info: "Get more info",
    talk_to_sales: "Get more info", // legacy alias
    request_pricing: "Request pricing",
  };
  const title = labels[action] || "Get in touch";
  const areas = ctx?.event?.areas_of_interest || [];

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/lead", {
        org_slug: orgSlug,
        event_slug: eventSlug || null,
        session_id: sessionId,
        action,
        ...form,
      });
      onSuccess();
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fade-up bg-black/30 border border-white/10 rounded-2xl p-5 mt-2">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/40">Action</div>
          <div className="font-display text-xl font-semibold">{title}</div>
        </div>
        <button onClick={onCancel} className="text-white/60 hover:text-white" data-testid="lead-close">
          <X size={20} />
        </button>
      </div>
      <p className="text-xs text-white/60 mb-4">Share your details and we'll be in touch.</p>
      <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
        {[
          { key: "full_name", label: "Full name", required: true, placeholder: "Jane Doe" },
          { key: "company", label: "Company", placeholder: "Acme Ltd." },
          { key: "email", label: "Email address", type: "email", required: true, placeholder: "you@acme.com" },
          { key: "phone", label: "Phone", type: "tel", placeholder: "+267 71 234 567" },
        ].map((f) => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/50">{f.label}</label>
            <input
              type={f.type || "text"}
              required={f.required}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-pink-400"
              data-testid={`lead-${f.key}`}
            />
          </div>
        ))}
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/50">Area of interest</label>
          {areas.length > 0 ? (
            <select
              value={form.area_of_interest}
              onChange={(e) => setForm({ ...form, area_of_interest: e.target.value })}
              className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-pink-400"
              data-testid="lead-area_of_interest"
            >
              <option value="" className="bg-purple-900">Select an option…</option>
              {areas.map((a) => <option key={a} value={a} className="bg-purple-900">{a}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={form.area_of_interest}
              onChange={(e) => setForm({ ...form, area_of_interest: e.target.value })}
              placeholder="e.g. ChatFlo Automation"
              className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-pink-400"
              data-testid="lead-area_of_interest"
            />
          )}
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full brand-gradient rounded-full py-3 font-medium text-sm mt-2 disabled:opacity-60"
          data-testid="lead-submit"
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </form>
    </div>
  );
}
